import type { ThemeConfig, ThemeTranslations } from './types'

/**
 * Creates a fresh default theme configuration
 * Returns a new object each time to prevent circular references
 */
function createDefaultTheme(): ThemeConfig {
  return {
    colors: {
      // Primary brand color - can be customized
      primary: '#0066cc',
      primaryDark: '#004d99',
      primaryLight: '#3385d6',

      // Secondary/accent color
      secondary: '#ffaa00',
      secondaryDark: '#cc8800',
      secondaryLight: '#ffbb33',

      // Semantic colors
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',

      // Neutral colors
      background: '#ffffff',
      surface: '#f9fafb',
      text: '#111827',
      textSecondary: '#6b7280',
      border: '#e5e7eb',
      divider: '#d1d5db',
    },

    typography: {
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontFamilyHeading: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontFamilyMono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',

      fontSize: {
        xs: '0.75rem',
        sm: '0.875rem',
        base: '1rem',
        lg: '1.125rem',
        xl: '1.25rem',
        '2xl': '1.5rem',
        '3xl': '1.875rem',
      },

      fontWeight: {
        light: 300,
        normal: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
      },

      lineHeight: {
        tight: 1.25,
        normal: 1.5,
        relaxed: 1.75,
      },
    },

    spacing: {
      xs: '0.25rem',
      sm: '0.5rem',
      md: '1rem',
      lg: '1.5rem',
      xl: '2rem',
      '2xl': '3rem',
      '3xl': '4rem',
    },

    borderRadius: {
      none: '0',
      sm: '0.25rem',
      md: '0.375rem',
      lg: '0.5rem',
      xl: '0.75rem',
      '2xl': '1rem',
      full: '9999px',
    },

    shadows: {
      sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      md: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      lg: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
      xl: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
      '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    },

    transitions: {
      fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
      normal: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
      slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
    },

    components: {
      articleCard: {
        imageHeight: '14rem', // h-56
        borderRadius: '0.75rem', // rounded-xl
        hoverShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
        ctaText: 'Read more',
      },
      breadcrumb: {
        background: 'linear-gradient(to right, rgba(255, 255, 255, 0.8), rgba(239, 246, 255, 0.5))',
        borderColor: 'rgba(229, 231, 235, 1)',
      },
    },
  }
}

/**
 * Default Theme Configuration
 * Use createDefaultTheme() internally to get fresh copies
 */
export const defaultTheme: ThemeConfig = createDefaultTheme()

/**
 * Default Translations (English)
 */
export const defaultTranslations: ThemeTranslations = {
  locale: 'en',
  breadcrumb: {
    home: 'Home',
    ariaLabel: 'Breadcrumb',
  },
  articleCard: {
    readMore: 'Read more',
    readCta: 'Read',
  },
  common: {
    loading: 'Loading...',
    error: 'Error',
    retry: 'Retry',
    signedInAs: 'Signed in as',
    signOut: 'Sign out',
  },
}
